import React, { FunctionComponent, useEffect } from 'react'
import Button from '@material-ui/core/Button'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Grid from '@material-ui/core/Grid'
import useFormal from '@kevinwolf/formal-web'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useApi } from 'components/providers/ApiProvider'
import { useMessage } from 'components/elements/Message'
import { DraggableDialog, DraggableDialogTitle } from 'components/elements/DraggableDialog'
import { TextField } from 'components/elements/TextField'
import { PlateNumberField } from 'Elements/PlateNumberField'
import { useCustomerNotificationExclusions } from 'CustomerNotificationExclusions/CustomerNotificationExclusionsProvider'

type Props = {
  open: boolean
}
type Functions = {
  setOpen(value: boolean): void
}
type AllProps = Props & Functions

const AddCustomerNotificationExclusionDialog: FunctionComponent<AllProps> = ({ open, setOpen }) => {
  const { t } = useTranslation()
  const api = useApi()
  const { setLoadList } = useCustomerNotificationExclusions()
  const { showSuccess, showError } = useMessage()

  const handleClose = () => {
    setOpen(false)
  }

  const initialValues = {
    mobillyClientId: '',
    plateNumber: '',
  }

  const schema = yup.object().shape({
    mobillyClientId: yup.lazy(value =>
      value === ''
        ? yup.string().required()
        : yup.number().required().integer().min(1),
    ),
    plateNumber: yup.string().required().uppercase(),
  })

  const formal = useFormal(initialValues, {
    schema,
    onSubmit: async values => {
      values = schema.cast(values)

      await api.createCustomerNotificationExclusion({
        mobilly_client_id: values.mobillyClientId,
        plate_number: values.plateNumber,
      })
        .then(data => {
          if (!data || !data.status) {
            throw new Error
          }

          handleClose()

          formal.change('mobillyClientId', initialValues.mobillyClientId)
          formal.change('plateNumber', initialValues.plateNumber)

          setLoadList()

          showSuccess(t('messages.exclusionAdded'))
        })
        .catch(error => {
          let message

          switch (error.code) {
            case 3:
              message = t('alerts.checkInput')
              break
            case 800:
              message = t('alerts.customerNotificationExclusionAlreadyCreated')
              break
            case 801:
              message = values.plateNumber === '*'
                ? t('alerts.customerNotificationExclusionConflictsWithPlateNumbers')
                : t('alerts.customerNotificationExclusionConflictsWithAllPlateNumbers')
              break
            default:
              message = t('alerts.tryLater')
          }

          showError(`${t('alerts.couldNotAddExclusion')} ${message}`)
        })
    },
  })

  return (
    <DraggableDialog fullWidth open={open} onClose={handleClose}>
      <form {...formal.getFormProps()} noValidate>
        <DraggableDialogTitle>{t('newExclusion')}</DraggableDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <TextField
              GridProps={{ xs: 12 }}
              type="number"
              label={t('mobillyClientId')}
              required
              inputProps={{
                min: 1,
              }}
              {...formal.getFieldProps('mobillyClientId')}
              autoFocus={true}
            />
            <PlateNumberField
              GridProps={{ xs: 12 }}
              required
              allowAsterisk={true}
              helperText={<><span style={{fontFamily: '"Roboto Mono", monospace'}}>*</span> {`— ${t('allPlateNumbers')}`}</>}
              inputProps={{
                maxLength: 10,
              }}
              {...formal.getFieldProps('plateNumber')}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleClose}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={formal.isSubmitting}
          >
            {t('add')}
          </Button>
        </DialogActions>
      </form>
    </DraggableDialog>
  )
}

export default AddCustomerNotificationExclusionDialog
